import { useCallback, useEffect, useRef, useState } from "react";

export function useFormFields(initialState) {
  const [fields, setValues] = useState(initialState);

  return [
    fields,
    function(event) {
      setValues({
        ...fields,
        [event.target.id]: event.target.value
      });
    }
  ];
}

export function usePolling(pollFunc) {
  // Create a hook for polling a function at regular intervals.
  // Start polling by calling setPollTime with a positive number of seconds.
  // Stop polling by calling setPollTime with 0 seconds.
  // Polling stops automatically if the polled function returns true.
  //
  // Returns an array of three members:
  //   pollTime          - Time in seconds to poll for.
  //   setPollTime       - Function to set pollTime to a new value.  0 disables polling.
  //   pollsCompleted    - Number of polls completed since polling started.  The counter
  //                       resets if setPollTime is called with a new interval.
  //   setPollsCompleted - Function to set pollsCompleted.
  const [pollTime, setPollTime] = useState(0);
  const [userPollTime, setUserPollTime] = useState(0);
  const [pollsCompleted, setPollsCompleted] = useState(0);

  useEffect(() => {
    async function doPolling() {
      // Call poll function and stop polling if it returns true
      if (await pollFunc() === true) {
        setPollTime(0);
        setUserPollTime(0);
      }
      // Increment polls completed
      setPollsCompleted(pollsCompleted + 1);
    }

    var pollWithTime = pollTime;
    if (userPollTime != pollTime) {
      // User updated polling time.  Reset polls completed
      setPollTime(userPollTime);
      setPollsCompleted(0);
      pollWithTime = userPollTime;
    }

    if (pollWithTime > 0) {
      // Call polling function once immediately
      doPolling();
      // Keep polling with the given interval
      const intervalId = setInterval(doPolling, pollWithTime * 1000);
      // Stop polling when component is unmounted
      return () => clearInterval(intervalId);
    }
  }, [userPollTime]);

  return [pollTime, setUserPollTime, pollsCompleted, setPollsCompleted];
}

// useStableCallback() adapted from: https://gist.github.com/rolandcoops/4364be2eff3586b0f8f3d0c10dc3be61
const throwReferenceError = () => {
  throw new ReferenceError('Callback was called directly while rendering, pass it as a callback prop instead.')
}

/**
 * Custom hook that wraps an unstable callback inside a stable wrapper callback.
 * Useful to e.g. keep an event handler prop stable when passing it down.
 *
 * @param {Function} callback - unstable callback to be wrapped
 * @param {Array} deps - list of values whose change will update the unstable callback reference (see React.useCallback)
 * @returns {Function} stable wrapped callback
 *
 * @example
 * // `handleClick` reference will not change even if deps change:
 * const handleClick = useStableCallback((event) => {
 *   console.log(someDep, event.target)
 * }, [someDep])
 */
export function useStableCallback(callback, deps) {
  const ref = useRef(throwReferenceError)

  // update stored callback ref if callback or deps change
  useEffect(() => {
    ref.current = callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback, ...deps])

  // return stable wrapped callback
  return useCallback((...args) => {
    return ref.current(...args)
  }, [ref])
}
